import React, { useState } from 'react';
import '../styles/index.css';
import { useTheme } from '../hooks/useTheme';
import { useLookup, STAGES } from '../hooks/useLookup';
import SearchForm from './SearchForm';
import { ProductCard, ProgressView, ResultsTable, EmptyState, ErrorCard } from './index';

export default function App() {
  const { theme, toggle } = useTheme();
  const [inputValue, setInputValue] = useState('');
  const [marketplace, setMarketplace] = useState('com');
  const { stage, message, product, results, liveKeywords, progressPct, error, lookup, reset } = useLookup();

  const isRunning = [STAGES.SCRAPING, STAGES.EXTRACTING, STAGES.EXPANDING, STAGES.RANKING].includes(stage);
  const isDone = stage === STAGES.COMPLETE;
  const hasError = stage === STAGES.ERROR;
  const isIdle = stage === STAGES.IDLE;

  function handleSubmit(asin, mkt) {
    setInputValue(asin);
    setMarketplace(mkt);
    lookup(asin, mkt);
  }

  function handleReset() {
    reset();
  }

  return (
    <div className="app-wrapper">
      {/* Header */}
      <header className="header">
        <div className="header-brand">
          <div className="brand-icon" aria-hidden="true">⌕</div>
          <span className="brand-name">KeyScope</span>
        </div>
        <button
          className="theme-btn"
          onClick={toggle}
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
        >
          {theme === 'light' ? '☾' : '○'}
        </button>
      </header>

      {/* Hero */}
      {isIdle && (
        <section className="hero">
          <div className="hero-eyebrow">
            <span className="live-dot" aria-hidden="true"></span>
            Free · No login · No limits on insights
          </div>
          <h1 className="hero-title">
            See what keywords your<br />
            competitors <em>actually rank for</em>
          </h1>
          <p className="hero-sub">
            Paste any Amazon ASIN and uncover the organic keywords driving their sales.
            The same intelligence Helium 10 charges for — completely free.
          </p>
        </section>
      )}

      {(isDone || isRunning) && (
        <div style={{ paddingTop: 28 }} />
      )}

      {/* Search Form */}
      <SearchForm
        onSubmit={handleSubmit}
        isRunning={isRunning}
        onReset={handleReset}
        isDone={isDone || hasError}
        defaultASIN={inputValue}
        defaultMarketplace={marketplace}
      />

      {/* Progress */}
      {isRunning && (
        <ProgressView
          stage={stage}
          message={message}
          progressPct={progressPct}
          liveKeywords={liveKeywords}
        />
      )}

      {/* Product info */}
      {product && (isDone || isRunning) && (
        <ProductCard product={product} />
      )}

      {/* Results */}
      {isDone && results.length > 0 && (
        <ResultsTable results={results} asin={inputValue} />
      )}

      {isDone && results.length === 0 && (
        <div className="error-card" style={{ borderColor: 'var(--border)' }}>
          <div className="error-icon">🔍</div>
          <div className="error-title">No ranking keywords found</div>
          <p className="error-msg">
            We checked the top keyword candidates but didn't find this product ranking
            in Amazon search results. This can happen if the product is new, in a very
            niche category, or region-restricted. Try a different marketplace.
          </p>
        </div>
      )}

      {/* Error */}
      {hasError && <ErrorCard message={error} />}

      {/* How it works */}
      {isIdle && <EmptyState />}

      {/* Footer */}
      <footer className="footer">
        <p className="footer-text">
          This tool is built using publicly available data, with care for Amazon sellers who deserve better tools. It has no affiliation with Amazon.
          <br />
          Found this useful? Or did you get a bug? Leave a message using the form below — I'd love to hear from you.
        </p>
      </footer>
    </div>
  );
}
